import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  getTranscriptHighlightPhase,
  tokenizeTranscriptWords,
  wordRangesBySpeechRate,
} from "../../../utils/transcription";
import {
  liveSessionKaraokeBodyStyles,
  liveSessionKaraokeTokenStyles,
} from "./styles";

const LIVE_KARAOKE_CHARS_PER_SEC = 16;

interface LiveTranscriptKaraokeTextProps {
  text: string;
  currentTimeSec?: number;
  durationSec?: number;
  isPlaying?: boolean;
  isComplete?: boolean;
}

// Karaoke word highlighting for the video session sidebar.
export const LiveTranscriptKaraokeText: React.FC<
  LiveTranscriptKaraokeTextProps
> = ({
  text,
  currentTimeSec = 0,
  durationSec = 0,
  isPlaying = false,
  isComplete = false,
}) => {
  const tokens = useMemo(() => tokenizeTranscriptWords(text), [text]);
  const ranges = useMemo(
    () => wordRangesBySpeechRate(tokens, LIVE_KARAOKE_CHARS_PER_SEC),
    [tokens],
  );

  // Finished lines stay fully blue (same as "past" during karaoke).
  if (isComplete || tokens.length === 0) {
    if (tokens.length === 0) {
      return <Typography sx={liveSessionKaraokeBodyStyles}>{text}</Typography>;
    }
    return (
      <Typography sx={liveSessionKaraokeBodyStyles}>
        {tokens.map((token, i) => (
          <React.Fragment key={`tok-${i}-${token}`}>
            {i > 0 ? " " : null}
            <Box component="span" sx={liveSessionKaraokeTokenStyles("past")}>
              {token}
            </Box>
          </React.Fragment>
        ))}
      </Typography>
    );
  }

  const karaokeActive =
    durationSec > 0 &&
    (isPlaying || currentTimeSec > 0.02 || (!isPlaying && currentTimeSec === 0));

  if (!karaokeActive) {
    return <Typography sx={liveSessionKaraokeBodyStyles}>{text}</Typography>;
  }

  // Before audio starts: keep all words in "future" (muted preview).
  const t = isPlaying || currentTimeSec > 0.02 ? currentTimeSec : -0.001;

  return (
    <Typography sx={liveSessionKaraokeBodyStyles}>
      {tokens.map((token, i) => {
        const phase = getTranscriptHighlightPhase(
          t,
          ranges[i].start_sec,
          ranges[i].end_sec,
        );
        return (
          <React.Fragment key={`tok-${i}-${token}`}>
            {i > 0 ? " " : null}
            <Box component="span" sx={liveSessionKaraokeTokenStyles(phase)}>
              {token}
            </Box>
          </React.Fragment>
        );
      })}
    </Typography>
  );
};
